import React from 'react'
import { css } from '@emotion/core'
import { GuestRoomAssignStatusResponseType } from '@/models/room-manager/response/guest-room-assign-status'
import { listRowRoomNumbers } from '@/utils/room-assign-rows'
import { useTranslation } from 'react-i18next'
import { RoomNameCollapseToggle } from '@/components/organisms/room-manager/assign-panel/room-name-collapse-toggle'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import { RadioSquareGroupField } from '@/components/molecules/radio-square-group-field'

type LeftGuestRoomProps = {
  guestRooms: GuestRoomAssignStatusResponseType['guestRoomAssignStatusList'] | undefined
  order: 'room' | 'floor'
  setOrder: (value: 'room' | 'floor') => void
  hiddenRoomNameList: string[]
  setHiddenRoomNameList: (value: string[]) => void
  onClickNewReservation: () => void
  /** 領域の右端に置く一括操作メニュー。表示可否は呼び出し側で決める */
  bulkOperationMenu?: React.ReactNode
}

export const LeftGuestRoom: React.FC<LeftGuestRoomProps> = ({
  guestRooms,
  order,
  setOrder,
  hiddenRoomNameList,
  setHiddenRoomNameList,
  onClickNewReservation,
  bulkOperationMenu,
}) => {
  const { t } = useTranslation()

  // Show/hide sidebar when clicked
  const onClickLabelName = (labelName: string) => {
    // Add to hiddenRoomNameList if labelName is not in hiddenRoomNameList
    if (!hiddenRoomNameList.includes(labelName)) {
      changeHiddenRoomNameList('add', labelName)
    } else {
      // Remove from hiddenRoomNameList if any
      changeHiddenRoomNameList('delete', labelName)
    }
  }

  /**
   * For switching the display of the sidebar
   */
  const changeHiddenRoomNameList = (type: 'add' | 'delete', labelName?: string) => {
    if (!guestRooms) {
      return
    }

    // Add new
    if (type === 'add') {
      if (labelName) {
        // If there are room types add them to the array
        setHiddenRoomNameList(hiddenRoomNameList.concat(labelName))
      } else {
        setHiddenRoomNameList(Object.keys(guestRooms).filter(Boolean))
      }
    }
    if (type === 'delete') {
      if (labelName) {
        // If there is a room type, remove it from the array
        setHiddenRoomNameList(hiddenRoomNameList.filter(name => name !== labelName))
      } else {
        // Initialize if there is no room type
        setHiddenRoomNameList([])
      }
    }
  }

  return (
    <div id="leftBlock" css={roomListRoomWrapperStyle}>
      <div>
        <div css={roomListBlockStyle}>
          <div css={roomListActionStyle}>
            <Button
              buttonType={ButtonType.Primary}
              width={120}
              height={32}
              fontSize={12}
              icon={require('@/static/images/new_reservation.svg')}
              onClick={() => onClickNewReservation()}
            >
              {t('New reservation')}
            </Button>
            {bulkOperationMenu && <div css={bulkOperationMenuStyle}>{bulkOperationMenu}</div>}
          </div>

          <RadioSquareGroupField
            value={order}
            name="order-toggle"
            items={[
              { label: t('Room order'), value: 'room' },
              { label: t('Floor order'), value: 'floor' },
            ]}
            onChange={v => {
              changeHiddenRoomNameList('delete')
              setOrder(v)
            }}
            itemStyle={css({
              label: { fontSize: 12, width: 68 },
              input: {
                ':checked': {
                  '+label': { border: 'none', color: '#676767' },
                },
              },
            })}
          />
        </div>
        <div css={remainingRoomTotalStyle}>
          <RoomNameCollapseToggle
            onClick={isHidden => {
              if (isHidden) {
                changeHiddenRoomNameList('delete')
              } else {
                changeHiddenRoomNameList('add')
              }
            }}
            collapseListItem={hiddenRoomNameList}
            allCollapseItemNum={guestRooms ? Object.keys(guestRooms).filter(Boolean).length : 0}
          />
          <div className="total-remaining-text">{t('Indicators.RemainingRooms')}</div>
        </div>
        {guestRooms &&
          Object.keys(guestRooms).map(floor => {
            if (!floor) return null
            return (
              <div key={floor} style={{ cursor: 'pointer' }}>
                <div css={roomListFloorWrapperStyle} onClick={() => onClickLabelName(floor)}>
                  <p css={roomListFloorStyle}>{floor}</p>
                </div>
                {listRowRoomNumbers(guestRooms[floor]).map(room => {
                  return (
                    !hiddenRoomNameList.includes(floor) && (
                      <div key={room} css={order === 'floor' ? roomWrapperStyle : roomWrapperRoomTypeStyle}>
                        {order === 'floor' ? (
                          <ul>
                            <li css={roomNumberStyle}>{guestRooms[floor][room].roomNumber}</li>
                            <li css={roomTypeStyle}>{guestRooms[floor][room].originRoomType}</li>
                          </ul>
                        ) : (
                          <ul>
                            <li css={roomNumberStyle}>{guestRooms[floor][room].roomNumber}</li>
                          </ul>
                        )}
                      </div>
                    )
                  )
                })}
              </div>
            )
          })}
      </div>
    </div>
  )
}

const roomListRoomWrapperStyle = css({
  minWidth: 200,
  position: 'sticky',
  left: 0,
  zIndex: 10,
})

const roomListFloorWrapperStyle = css({
  display: 'flex',
  alignItems: 'center',
  height: 47,
  background: '#fafafa',
  padding: '8px 17px 8px 24px',
  borderBottom: '1px solid #E3E3E3',
})
const roomListFloorStyle = css({
  fontSize: 12,
  letterSpacing: 0.6,
  fontWeight: 'bold',
  color: '#676767',
  display: '-webkit-box',
  '-webkit-line-clamp': '2',
  '-webkit-box-orient': 'vertical',
  overflow: 'hidden',
  lineHeight: '15px',
})

const roomListBlockStyle = css({
  borderRight: '1px solid #F2F2F2',
  borderBottom: '1px solid #E3E3E3',
  height: 90,
  background: '#FFFFFF',
  position: 'sticky',
  top: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-around',
})

// 「新規予約」は従来どおり中央に置き、一括操作メニューは中央寄せの計算から外して右端に固定する
const roomListActionStyle = css({
  position: 'relative',
  alignSelf: 'stretch',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

const bulkOperationMenuStyle = css({
  position: 'absolute',
  top: '50%',
  right: 6,
  transform: 'translateY(-50%)',
})

const roomWrapperStyle = css({
  height: 42,
  padding: '8px 0 8px 22px',
  background: '#fff',
  borderRight: '1px solid #F2F2F2',
  borderBottom: '1px solid #F2F2F2',
  ':last-of-type': {
    borderBottom: '1px solid #E3E3E3',
  },
})

const roomWrapperRoomTypeStyle = css(roomWrapperStyle, {
  lineHeight: '25px',
  display: 'flex',
  alignItems: 'center',
  padding: '8px 17px 8px 22px',
})

const roomNumberStyle = css({
  fontSize: 12,
  letterSpacing: 0.6,
  marginBottom: 4,
  fontWeight: 'bold',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const roomTypeStyle = css({
  fontSize: 10,
  letterSpacing: 0.5,
  color: '#A3A3A3',
  fontWeight: 'bold',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const remainingRoomTotalStyle = css({
  backgroundColor: '#FAFAFA',
  height: 29,
  padding: 8,
  borderBottom: '1px solid #E3E3E3',
  borderRight: '1px solid #F2F2F2',
  position: 'sticky',
  top: 90,
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '.total-remaining-text': {
    color: '#A3A3A3',
    fontWeight: 'bold',
    fontSize: 12,
    letterSpacing: '0.6px',
  },
})
